import { notification } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UseMutateFunction, useMutation, useQueryClient } from 'react-query'

import { QUERY_KEYS } from '@api'
import { AxiosErrorType, BlocktrainUpdatePayload } from '@types'

import BlocktrainApi from './api'

const { put } = BlocktrainApi

interface UseUpdateBlocktrainProps {
  id: string | number
  close?: () => void
}
interface UseUpdateBlocktrainReturn {
  updateBlocktrain: UseMutateFunction<
    BlocktrainUpdatePayload,
    AxiosErrorType,
    BlocktrainUpdatePayload,
    unknown
  >
  updateBlocktrainIsFetching?: boolean
}

const useUpdateBlocktrain = ({
  id,
  close,
}: UseUpdateBlocktrainProps): UseUpdateBlocktrainReturn => {
  const { t } = useTranslation()
  const [updateBlocktrainIsFetching, setUpdateBlocktrainIsFetching] = useState<boolean>()

  const queryClient = useQueryClient()

  const onSuccess = () => {
    notification.success({
      message: t('new_blocktrain_edited'),
    })
    queryClient.invalidateQueries(QUERY_KEYS.BLOCKTRAINS)
    queryClient.invalidateQueries(QUERY_KEYS.BLOCKTRAIN)
    setUpdateBlocktrainIsFetching(false)
    if (close) {
      close()
    }
  }

  const onError = (err: AxiosErrorType) => {
    setUpdateBlocktrainIsFetching(false)
    if (err.status === 404) {
      console.error('Route not found', { err })
    }
    const errorResponse = err.data.error as {
      type: string
      status: number
      message: string
    }

    if (errorResponse.message) {
      notification.error({
        message: t(errorResponse.message),
      })
    } else {
      notification.error({
        message: t('cannot_update_blocktrain'),
      })
    }
  }

  const { mutate: updateBlocktrain } = useMutation<
    BlocktrainUpdatePayload,
    AxiosErrorType,
    BlocktrainUpdatePayload
  >(payload => put(id!, payload), {
    onSuccess,
    onError,
    onMutate: () => {
      setUpdateBlocktrainIsFetching(true)
    },
  })

  return {
    updateBlocktrain,
    updateBlocktrainIsFetching,
  }
}

export { useUpdateBlocktrain }
